'use client';

/**
 * EntityDetailsCard — chrome/selection/actions wrapper for any
 * EntityDetailResponse; rendering itself is delegated to the
 * schema-driven DetailsPanel (entity_schema.py is the source of truth
 * for what sections exist). This component must never hand-roll a
 * section — that duplicated entity_schema.py once already and silently
 * fell out of sync with it.
 *
 * Entity-specific actions (e.g. MCP edit/download/delete) are
 * conditionally wired based on entity type from viewport selection.
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain copilot
 */

import { useMemo } from 'react';
import { Pencil, Download, FileSpreadsheet, Trash2, Loader2 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { useMcpActions } from '@/lib/hooks/use-mcp-actions';
import { useFocusedRequirementStore } from '@/lib/stores/focused-requirement-store';
import { RailCard } from './rail-card';
import { DetailsPanel } from '../details/details-panel';

interface DateField {
  label: string;
  value: string | null;
}

interface Party {
  role: string;
  name: string | null;
  semantic_id: string | null;
}

interface LineItem {
  product_name: string | null;
  quantity: number | null;
  unit_price: number | null;
  total: number | null;
  vendor_name: string | null;
  // Present on cost-plan line items (costplan_details) only — the
  // requirement this candidate was picked to satisfy, and its live
  // human label. See shared.tsx's LineItem docstring.
  source_line_sid?: string | null;
  source_line_label?: string | null;
}

interface LinkedDoc {
  semantic_id: string | null;
  name: string | null;
  status: string | null;
  doc_type: string | null;
}

interface EntityDetail {
  semantic_id: string;
  entity_type: string;
  name: string | null;
  status: string | null;
  amount_total: number | null;
  currency: string | null;
  dates: DateField[];
  parties: Party[];
  line_items: LineItem[];
  line_count: number;
  notes: string | null;
  metadata: Record<string, unknown>;
  linked_rfqs: LinkedDoc[];
}

interface EntityDetailsCardProps {
  strategyName?: string;
  /**
   * Explicit fetch-input override — same card, parameterized for a
   * second surface (mental move 6: a self-fetching card takes its
   * fetch inputs as live props, never frozen internal state). When
   * set, these win over `useViewportSelection()`'s page selection —
   * e.g. the Cost Plan rail surface (mode3/mode11) points this card
   * at the PINNED MCP id instead of whatever is selected on canvas.
   * Omit both to keep the original mode1 behaviour (reads page
   * selection).
   */
  entityId?: string | null;
  entityType?: string | null;
  /**
   * Turns on click-to-focus on this card's `items` section (a cost
   * plan's line items) — clicking a row focuses that row's
   * requirement (`source_line_sid`) in `focused-requirement-store`,
   * so a product tile elsewhere knows which requirement to append to.
   * Defaults to OFF: mode1's existing `mcp_details` rail usage
   * (card-registry.tsx) never passes this, so it keeps rendering
   * exactly as it does today. Only the Cost Plan rail surface
   * (mode3/mode11, cost-plan-surface.tsx) turns it on.
   */
  interactive?: boolean;
}

export function EntityDetailsCard({
  strategyName,
  entityId: entityIdOverride,
  entityType: entityTypeOverride,
  interactive = false,
}: EntityDetailsCardProps = {}) {
  const { label, Icon, strategy: chromeStrategy, contentKey } = useCardIdentity();
  const viewportSelection = useViewportSelection();
  const entityId = entityIdOverride ?? viewportSelection.entityId;
  const entityType = entityTypeOverride ?? viewportSelection.entityType;
  const resolvedStrategy = strategyName ?? chromeStrategy;

  const { data: raw, isLoading } = useStrategyData<EntityDetail>(
    resolvedStrategy ?? '',
    { semantic_id: entityId ?? '' },
    !!entityId && !!resolvedStrategy,
  );

  const { editMcp, deleteMcp, downloadMcp, downloadMcpScheduleXlsx } = useMcpActions();
  const isMcp = entityType === 'mcp';

  const focusedSourceLineSid = useFocusedRequirementStore((s) => s.focusedSourceLineSid);
  const setFocused = useFocusedRequirementStore((s) => s.setFocused);
  const handleItemClick = (item: Record<string, unknown>) => {
    const sourceLineSid = item.source_line_sid as string | null | undefined;
    if (!sourceLineSid) return;
    const label = (item.source_line_label as string | null | undefined)
      ?? (item.product_name as string | null | undefined)
      ?? null;
    setFocused(sourceLineSid, label);
  };

  const actions = entityId && !isLoading ? (
    <div className="flex items-center gap-0.5">
      {isMcp && (
        <>
          <button
            type="button"
            onClick={() => void editMcp(entityId)}
            aria-label="Edit"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => void downloadMcp(entityId, raw?.data?.name)}
            aria-label="Download PDF"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <Download className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => void downloadMcpScheduleXlsx(entityId, raw?.data?.name)}
            aria-label="Download Excel"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <FileSpreadsheet className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => void deleteMcp(entityId, raw?.data?.name)}
            aria-label="Delete"
            className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </>
      )}
    </div>
  ) : undefined;

  const title = label ?? 'Details';

  // Scene export — register the entity-details card (obeya_costplan on canvas,
  // mcp_details on the rail) into the capture envelope.
  useCardExport(useMemo(() => {
    const d = raw?.data;
    return {
      content_key: contentKey ?? 'entity_details',
      status: (d ? 'loaded' : entityId && resolvedStrategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
      query_key: entityId && resolvedStrategy
        ? ['strategy-data', resolvedStrategy, { semantic_id: entityId }]
        : null,
      text: d?.name ?? title,
      json: { entity_type: entityType ?? null, entity_id: entityId ?? null, name: d?.name ?? null },
    };
  }, [contentKey, raw, entityId, resolvedStrategy, entityType, title]));

  if (!entityId) {
    return (
      <RailCard title={title} icon={Icon}>
        <div className="text-xs text-muted-foreground">Select an item to see details</div>
      </RailCard>
    );
  }

  if (isLoading) {
    return (
      <RailCard title={title} icon={Icon}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </div>
      </RailCard>
    );
  }

  const d = raw?.data;
  if (!d) {
    return (
      <RailCard title={title} icon={Icon}>
        <div className="text-xs text-destructive">No data</div>
      </RailCard>
    );
  }

  // Delegate rendering to the schema-driven DetailsPanel — entity_schema.py
  // is the source of truth for what sections exist and how they're laid
  // out. This component's job is selection/chrome/actions only; it must
  // never hand-roll a section a schema change should have shipped for free.
  return (
    <RailCard title={d.name ?? title} icon={Icon} actions={actions}>
      <DetailsPanel
        entityType={entityType ?? 'mcp'}
        entityId={entityId}
        data={d as unknown as Record<string, unknown>}
        isLoading={false}
        interactive={interactive}
        focusedSourceLineSid={focusedSourceLineSid}
        onItemClick={interactive ? handleItemClick : undefined}
      />
    </RailCard>
  );
}
